import { CiSearch } from 'react-icons/ci';
import { Badge, Button, Input, Modal, Table } from '../components';
import React, { useEffect, useState } from 'react';
import { Enum, Pagination, Ticket as ITicket } from '../../interfaces';
import { getTicketStates } from '../../services';
import { getTickets } from './ticket.service';
import { ConfigProvider, Divider, Pagination as AntPagination, Spin, TableProps } from 'antd';
import moment from 'jalali-moment';
import fa_IR from 'antd/locale/fa_IR';
import { AddTicketModal } from './addTicketModal/addTicketModal.index';
import { Card } from '../../utilities/components';
import { Link } from 'react-router-dom';
import { MdFilterList } from 'react-icons/md';
import { Select } from '../components/select/select.index';
import { DatePicker, JalaliLocaleListener } from 'antd-jalali';
import dayjs from 'dayjs';
import { convertFaToEnCalendar } from '../../scripts';

export const Ticket = () => {
    const [tickets, setTickets] = useState<Pagination<ITicket>>();
    const [loading, setLoading] = useState<boolean>(false);
    const [keyword, setKeyword] = useState('');
    const [ticketStates, setTicketStates] = useState<Enum[]>();
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [addTicketModalOpen, setAddTicketModalOpen] = useState<boolean>(false);
    const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);
    const [selectedTicketState, setSelectedTicketState] = useState<Enum>();
    const [createOnFrom, setCreateOnFrom] = useState<dayjs.Dayjs | null>(null);
    const [createOnTo, setCreateOnTo] = useState<dayjs.Dayjs | null>(null);

    const columns: TableProps<ITicket>['columns'] = [
        {
            title: 'ردیف',
            dataIndex: 'id',
            render: (text, record, index) => <span className={'font-medium text-sm'}>{index + 1}</span>,
        },
        {
            title: 'عنوان تیکت',
            dataIndex: 'title',
            render: (text, record) => <span className={'text-darkPurple-100 text-sm font-bold'}>{record.title}</span>,
        },
        {
            title: 'شماره تیکت',
            dataIndex: 'ticketNumber',
            render: (text, record) => (
                <span className={'font-medium text-sm text-black-60 en'}>{record.ticketNumber}</span>
            ),
        },
        {
            title: 'وضعیت',
            dataIndex: 'state',
            align: 'center',
            render: (text, record: ITicket) => (
                <Badge
                    noIcon
                    className={'w-fit mx-auto'}
                    type={
                        record.state.id === 1
                            ? 'info'
                            : record.state.id === 2
                                ? 'success'
                                : record.state.id === 3
                                    ? 'danger'
                                    : undefined
                    }
                    title={record.state.title}
                />
            ),
        },
        {
            title: 'تاریخ',
            dataIndex: 'createdOn',
            align: 'center',
            render: (text, record: ITicket) => (
                <span className={'font-medium text-sm'}>
                    {moment.utc(record?.createdOn).locale('fa').local().format('jD jMMMM jYYYY HH:mm')}
                </span>
            ),
        },
        {
            title: 'جزئیات',
            dataIndex: 'details',
            align: 'center',
            render: (text, record) => (
                <Link to={record.id} className={'h-8 text-sm mx-auto text-primary-100 clickable'} type={'text'}>
                    مشاهده
                </Link>
            ),
        },
    ];

    const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
    };

    const handlePageChange = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const buildQueryString = () => {
        const params = new URLSearchParams();

        if (selectedTicketState) {
            params.append('state', selectedTicketState.id.toString());
        }
        if (keyword) {
            params.append('keywords', keyword);
        }
        if (createOnFrom) {
            const jalaliDate = createOnFrom?.format('YYYY/MM/DD');
            params.append('createOnFrom', convertFaToEnCalendar(jalaliDate));
        }
        if (createOnTo) {
            const jalaliDate = createOnTo?.format('YYYY/MM/DD');
            params.append('createOnTo', convertFaToEnCalendar(jalaliDate));
        }

        return params.toString();
    };

    const fetchData = (page: number, pageSize: number) => {
        const queryString = buildQueryString();

        const finalQueryString = queryString ? `?${queryString}` : '';

        getTickets(setLoading, setTickets, page, pageSize, finalQueryString);
    };

    useEffect(() => {
        getTicketStates(setLoading, setTicketStates);
    }, []);

    useEffect(() => {
        fetchData(page, pageSize);
    }, [page, pageSize]);

    return (
        <div className={'flex flex-col p-6'}>
            <AddTicketModal
                open={addTicketModalOpen}
                setOpen={setAddTicketModalOpen}
                onComplete={() => fetchData(page, pageSize)}
            />
            <Modal open={filterModalOpen} setOpen={setFilterModalOpen} title={'جستجوی پیشرفته'}>
                <Divider />
                <label className={'font-bold text-base text-black-100'}>وضعیت:</label>
                <Select
                    className={'mt-2'}
                    value={selectedTicketState?.id}
                    onChange={(value) => {
                        const selectedState = ticketStates?.find((state) => state.id === value);
                        setSelectedTicketState(selectedState);
                    }}
                    allowClear
                    placeholder="یک وضعیت انتخاب کنید"
                    style={{ width: '100%' }}
                >
                    {ticketStates?.map((state) => (
                        <Select.Option key={state.id} value={state.id}>
                            {state.title}
                        </Select.Option>
                    ))}
                </Select>
                <div className={'flex flex-col w-full mt-6'}>
                    <label className="font-bold text-base text-black-100 mb-2 max-lg:text-sm">از تاریخ:</label>
                    <ConfigProvider locale={fa_IR} direction={'rtl'}>
                        <JalaliLocaleListener />
                        <DatePicker
                            value={createOnFrom}
                            className={
                                'border border-primary-40 rounded-[28px] h-14 max-lg:h-12 text-base max-lg:text-sm px-3'
                            }
                            placeholder={'تاریخ شروع را انتخاب کنید'}
                            onChange={(date: any) => {
                                setCreateOnFrom(date);
                            }}
                        />
                    </ConfigProvider>
                </div>
                <div className={'flex flex-col w-full mt-6'}>
                    <label className="font-bold text-base text-black-100 mb-2 max-lg:text-sm">تا تاریخ:</label>
                    <ConfigProvider locale={fa_IR} direction={'rtl'}>
                        <JalaliLocaleListener />
                        <DatePicker
                            value={createOnTo}
                            className={
                                'border border-primary-40 rounded-[28px] h-14 max-lg:h-12 text-base max-lg:text-sm px-3'
                            }
                            placeholder={'تاریخ پایان را انتخاب کنید'}
                            onChange={(date: any) => {
                                setCreateOnTo(date);
                            }}
                        />
                    </ConfigProvider>
                </div>
                <Button
                    type={'primary'}
                    className={'w-full mt-6'}
                    onClick={() => {
                        fetchData(page, pageSize);
                        setFilterModalOpen(false);
                    }}
                >
                    اعمال فیلتر
                </Button>
            </Modal>
            <div className={'flex items-center justify-between h-12 max-lg:h-auto max-lg:flex-col max-lg:gap-4'}>
                <div className={'flex items-center gap-4 max-lg:w-full'}>
                    <Input
                        wrapperClassName={'h-12'}
                        className={'w-72 max-lg:w-full'}
                        size={'small'}
                        placeholder={'کلمه ی کلیدی را جستجو کنید'}
                        prefix={<CiSearch />}
                        value={keyword}
                        onChange={handleKeywordChange}
                        onPressEnter={() => fetchData(page, pageSize)}
                    />
                    <Button
                        type={selectedTicketState || createOnFrom || createOnTo ? 'primary' : 'default'}
                        icon={<MdFilterList />}
                        iconPosition={'end'}
                        className={'!h-12 max-lg:hidden'}
                        onClick={() => setFilterModalOpen(true)}
                    >
                        فیلتر پیشرفته
                    </Button>
                    <Button
                        type={selectedTicketState || createOnFrom || createOnTo ? 'primary' : 'default'}
                        icon={<MdFilterList />}
                        className={'!h-12 !min-w-12 !rounded-full lg:hidden'}
                        onClick={() => setFilterModalOpen(true)}
                    ></Button>
                </div>
                <Button type={'primary'} className={'!h-12 max-lg:!w-full'} onClick={() => setAddTicketModalOpen(true)}>
                    ارسال تیکت جدید
                </Button>
            </div>
            <Card size={'small'} title={'تیکت های من'} className={'mt-6 max-lg:!hidden'} noPadding titleDivider>
                <Table<ITicket>
                    dataSource={tickets?.items}
                    className={'border-t-0 rounded-t-0 max-lg:hidden'}
                    columns={columns}
                    loading={loading}
                    rowKey={'id'}
                    pagination={false}
                />
                <ConfigProvider locale={fa_IR}>
                    <AntPagination
                        current={page}
                        pageSize={pageSize}
                        total={tickets?.pagination.totalCount}
                        onChange={handlePageChange}
                        className="mt-4 justify-center"
                        style={{ direction: 'ltr' }}
                        showSizeChanger={true}
                        onShowSizeChange={(current, size) => handlePageChange(current, size)}
                    />
                </ConfigProvider>
            </Card>
            {loading ? (
                <Spin spinning={loading} className={'lg:hidden'} />
            ) : (
                <ul className={'lg:hidden flex flex-col gap-4 bg-background mt-6'}>
                    <h4 className={'text-base font-bold text-darkPurple-100'}>تیکت های من</h4>
                    {tickets?.items.map((ticket) => (
                        <li
                            key={ticket.id}
                            className={'flex flex-col p-4 bg-white rounded-lg border border-primary-20'}
                        >
                            <div className={'flex justify-between'}>
                                <span className={'font-bold text-sm text-darkPurple-100'}>{ticket.title}</span>
                                <Badge
                                    noIcon
                                    className={'w-fit'}
                                    type={
                                        ticket.state.id === 1
                                            ? 'info'
                                            : ticket.state.id === 2
                                                ? 'success'
                                                : ticket.state.id === 3
                                                    ? 'danger'
                                                    : undefined
                                    }
                                    title={ticket.state.title}
                                />
                            </div>
                            <Divider className={'my-3'} />
                            <div className={'flex justify-between'}>
                                <div className={'flex flex-col items-start'}>
                                    <span className={'font-bold text-sm text-darkPurple-100'}>شماره تیکت:</span>
                                    <span
                                        className={
                                            'font-normal text-sm text-darkPurple-100 whitespace-nowrap text-ellipsis overflow-hidden en'
                                        }
                                    >
                                        {ticket.ticketNumber}
                                    </span>
                                </div>
                                <div className={'flex flex-col items-start'}>
                                    <span className={'font-bold text-sm text-darkPurple-100'}>زمان:</span>
                                    <span className={'font-medium text-sm text-darkPurple-100'}>
                                        {moment
                                            .utc(ticket?.createdOn)
                                            .locale('fa')
                                            .local()
                                            .format('jD jMMMM jYYYY HH:mm')}
                                    </span>
                                </div>
                                <Button className={'!h-8'} type={'default'} href={`/ticket/${ticket.id}`}>
                                    جزئیات
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            <ConfigProvider locale={fa_IR}>
                <AntPagination
                    current={page}
                    pageSize={pageSize}
                    total={tickets?.pagination.totalCount}
                    onChange={handlePageChange}
                    className="mt-4 justify-center lg:hidden"
                    style={{ direction: 'ltr' }}
                    showSizeChanger={true}
                    onShowSizeChange={(current, size) => handlePageChange(current, size)}
                />
            </ConfigProvider>
        </div>
    );
};
